import {
    CreateCustomStrategyTxnParams,
    DepositCollateralParams,
    formatDurationWithTypeShorthand,
    getDurationFromIndex,
    RefinanceLoanTxnParams,
    RepayLoanTxnParams,
    StrategyDuration,
    TokenListMetadata
} from "@bridgesplit/abf-sdk";
import { bpsToPercent, lamportsToUiAmount } from "@bridgesplit/utils";
import {
    AbfLoanExpanded,
    CreateLoanTxnParams,
    formatLeverage,
    getMintFromAssetData,
    LoopWindParams
} from "@bridgesplit/abf-react";
import {
    OrderSource,
    SubmitEarnData,
    SubmitRepayData,
    SubmitTopupData,
    TrackMarketBorrowData,
    TrackMarketLendData,
    TrackRefinanceData
} from "app/types";

export function trackSubmitMarketBorrowOrder({
    params,
    duration,
    source
}: {
    params: CreateLoanTxnParams;
    duration: StrategyDuration;
    source: OrderSource;
}): TrackMarketBorrowData {
    return {
        strategyAddress: params.principalRequested[0].strategy,
        apy: params.principalRequested[0].expectedLoanValues.expectedApy,
        collateral: [
            {
                assetMint: getMintFromAssetData(params.depositCollateral[0].collateralAssetData),
                totalAmount: params.depositCollateral[0].collateralAmount,
                assetType: 0,
                assetIdentifier: ""
            }
        ],
        principalMint: params.principalRequested[0].principalMint,
        principalAmount: params.principalRequested[0].principalAmount,
        duration: formatDurationWithTypeShorthand(duration.duration, duration.durationType),
        source
    };
}

export function trackSubmitMarketLend(
    params: CreateCustomStrategyTxnParams,
    offerTerms: FormattedOfferTerms
): TrackMarketLendData {
    return {
        offerTerms,
        principalMint: params.principalMint,
        amount: params.amount
    };
}

export function trackSubmitRepay(params: RepayLoanTxnParams, metadata: TokenListMetadata): SubmitRepayData[] {
    return params.repayParams.map((repayParam) => ({
        loanAddress: params.loan,
        amountUi: lamportsToUiAmount(repayParam.amount, metadata.decimals),
        amount: repayParam.amount,
        ledgerIndex: repayParam.ledgerIndex
    }));
}

export function trackSubmitRefinance(
    params: RefinanceLoanTxnParams,
    loanExpanded: AbfLoanExpanded,
    ledgerIndex: number
): TrackRefinanceData {
    const ledger = loanExpanded.ledger[ledgerIndex];

    return {
        strategyAddress: params.newStrategy,
        oldStrategyAddress: params.oldStrategy,
        principalMint: params.principalMint,
        collateral: loanExpanded.collateral.map((collateralInfo) => ({
            assetMint: collateralInfo.assetMint,
            assetIdentifier: collateralInfo.assetIdentifier,
            assetType: collateralInfo.assetType,
            totalAmount: collateralInfo.amount
        })),
        principalAmount: ledger.principalDue - ledger.principalRepaid,
        duration: formatDurationWithTypeShorthand(getDurationFromIndex(params.refinanceParams.durationIndex)),
        apy: bpsToPercent(ledger.apy),
        ltv: ledger.ltvRatios.map((ltv) => bpsToPercent(ltv))
    };
}

export function trackSubmitEarnVault({ params }: { params: LoopWindParams }): SubmitEarnData {
    return {
        loopExpanded: params.loopExpanded,
        vaultIdentifier: params.loopExpanded?.vaultIdentifier,
        collateralAmount: params.depositCollateral[0].collateralAmount,
        leverageMultiplier: formatLeverage(params.leverageMultiplier),
        strategyAddress: params.principalRequested[0].strategy,
        principalRequested: params.principalRequested[0].principalAmount,
        apy: bpsToPercent(params.principalRequested[0].expectedLoanValues.expectedApy),
        userPublicKey: params.userPublicKey
    };
}

export interface FormattedOfferTerms {
    collateral: string[];
    terms: {
        [key: string]: number;
    };
}

export function presetFormToMixpanelFormat(
    collateral: string[],
    strategyDurationToApy: Map<string, number | undefined>
): FormattedOfferTerms {
    const terms: { [key: string]: number } = {};

    for (const [duration, apy] of strategyDurationToApy.entries()) {
        if (apy) {
            terms[duration] = apy;
        }
    }

    return {
        collateral,
        terms
    };
}

export function trackSubmitTopup(
    params: DepositCollateralParams,
    metadata: TokenListMetadata,
    ledgerIndex: number
): SubmitTopupData {
    return {
        loanAddress: params.loan,
        collateralMint: params.depositMint,
        amount: lamportsToUiAmount(params.amount, metadata.decimals),
        decimals: metadata.decimals,
        expectedApy: bpsToPercent(params.expectedLoanValues.expectedApy),
        expectedLqt: params.expectedLoanValues.expectedLqt.map(bpsToPercent),
        ledgerIndex: ledgerIndex
    };
}
